import { useState, useEffect, useCallback } from 'react';
import { 
  getUserNotifications, 
  markAsRead, 
  markAllAsRead, 
  removeNotification, 
  subscribeToNotifications 
} from '../services/websocketApi/notifications';
import { useWebSocket } from '../contexts/WebSocketContext';

/**
 * Check if a notification is auth-related and should be filtered out
 * @param {Object} notification - The notification to check
 * @returns {boolean} - True if it's an auth notification that should be filtered out
 */
const isAuthNotification = (notification) => {
  // Check notification ID
  if (notification.notificationId && 
      (notification.notificationId.includes('ws-auth') || 
       notification.notificationId.includes('ws-connect'))) {
    return true;
  }
  
  // Check message content
  if (notification.message) {
    const messageLower = notification.message.toLowerCase();
    const authTerms = [
      'authentication successful', 
      'authenticated', 
      'authentication failed',
      'auth error',
      'websocket connected',
      'connection established',
      'logged in'
    ];
    
    return authTerms.some(term => messageLower.includes(term.toLowerCase()));
  }
  
  return false;
};

/**
 * React hook for working with notifications
 * @param {Object} options - Hook options
 * @param {boolean} [options.autoFetch=true] - Whether to automatically fetch notifications on mount
 * @returns {Object} - Notification state and methods
 */
const useNotifications = (options = {}) => {
  const { autoFetch = true } = options;
  
  // Get websocket connection status
  const { isAuthenticated, connectionStatus } = useWebSocket();
  
  // Track notifications state
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Function to fetch notifications
  const fetchNotifications = useCallback(async () => {
    // Skip if not authenticated
    if (!isAuthenticated || connectionStatus !== 'connected') {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getUserNotifications();
      
      // Filter out auth notifications before setting state
      const filteredData = Array.isArray(data)
        ? data.filter(notification => !isAuthNotification(notification))
        : [];
      
      setNotifications(filteredData);
      setLastFetched(new Date());
      return filteredData;
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message || 'Failed to fetch notifications');
      return [];
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, connectionStatus]);

  // Mark notification as read
  const handleMarkAsRead = useCallback(async (notificationId) => {
    try {
      await markAsRead(notificationId);
      
      // Update local state to mark notification as read
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true } 
            : notification
        )
      );
      
      return true;
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError(err.message || 'Failed to mark notification as read');
      return false;
    }
  }, []);

  // Mark all notifications as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      
      // Update local state to mark all notifications as read
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );
      
      return true;
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError(err.message || 'Failed to mark all notifications as read');
      return false;
    }
  }, []);

  // Remove a notification
  const handleRemoveNotification = useCallback(async (notificationId) => {
    try {
      await removeNotification(notificationId);
      
      // Update local state to remove the notification
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
      
      return true;
    } catch (err) {
      console.error('Error removing notification:', err);
      setError(err.message || 'Failed to remove notification');
      return false;
    }
  }, []);

  // Listen for new notifications
  useEffect(() => {
    if (!isAuthenticated || connectionStatus !== 'connected') {
      return;
    }
    
    // Handler for new notifications
    const handleNewNotification = (payload) => {
      if (payload && payload.data) {
        // Skip auth notifications
        if (isAuthNotification(payload.data)) {
          console.log('Skipping auth notification:', payload.data.message);
          return;
        }
        
        // Check if it's a duplicate by checking if we already have a notification with the same ID
        setNotifications(prev => {
          const notificationId = payload.data.id || payload.data.notificationId;
          const isDuplicate = prev.some(n => n.id === notificationId);
          
          if (isDuplicate) {
            return prev;
          }
          
          // Add the new notification at the beginning of the array
          return [payload.data, ...prev];
        });
      }
    };
    
    // Subscribe to new notifications
    const unsubscribe = subscribeToNotifications(handleNewNotification);
    
    // Cleanup subscription
    return () => {
      unsubscribe();
    };
  }, [isAuthenticated, connectionStatus]);

  // Initial fetch
  useEffect(() => {
    if (autoFetch && isAuthenticated && connectionStatus === 'connected') {
      fetchNotifications();
    }
  }, [fetchNotifications, autoFetch, isAuthenticated, connectionStatus]);

  // Calculate counts
  const unreadCount = notifications.filter(notification => !notification.isRead).length;
  
  return {
    notifications,
    loading,
    error,
    lastFetched,
    unreadCount,
    fetchNotifications,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    removeNotification: handleRemoveNotification,
  };
};

export default useNotifications; 